/* eslint-disable react/display-name, react/prop-types */

import React from 'react';
import { AsyncState } from 'react-async';
import Vendor from 'data/Vendor';
import FeatureFlag, { FeatureFlagValues } from 'data/FeatureFlag';
import User, { UserPreferences, hasOfmpAdminProfile } from 'data/User';
import { BusinessUnit } from 'data/BusinessUnit';
import { grantAccess } from 'utils/authorization';
import { ROUTES_VIEWER_ROLE } from 'constants/general';

// Common Components
import VendorsList from 'admin/VendorsList';
import UsersList from 'admin/UsersList';
import CategoriesList from 'admin/CategoriesList';
import AttributesList from 'admin/AttributesList';
import AttributesListV2 from 'admin/AttributesListV2';
import AccountSettings from 'common/AccountSettings';
import Dashboard from 'common/Dashboard';
import ProductReview from 'common/ProductReview';
import WarehouseList from 'common/WarehouseList';
import OrderDetails from 'components/Consignments/OrderDetails';
import OrderDocuments from 'components/Consignments/OrderDocuments';
import SplitOrderContainer from 'components/Orders/SplitOrderContainer';
import EditOrderContainer from 'components/Orders/EditOrder/EditOrderContainer';
import CompaniesList from 'common/Companies/CompaniesList';
import CompanyDetailsV2 from 'common/Companies/CompanyDetailsV2';
import CustomerDetails from 'common/Customers/CustomerDetails';
import CustomersList from 'common/Customers/CustomersList';
import MembersList from 'common/Affiliates/MembersList';
import MemberDetails from 'common/Affiliates/MemberDetails';
import AffiliateSettings from 'components/Affiliates/AffiliateSettings';
import OrdersList from 'components/Orders/OrderList/OrdersList';
import PowerBiReports from 'common/PowerBiReports/PowerBiReports';
import PricebooksTab from 'common/Companies/PricebooksTab';
import NotFound from 'components/UI/NotFound';
import UpsertVendor from 'components/Vendor/UpsertVendor';
import ProductListV2 from 'components/Product/ProductListing/ProductListingComponent';
import ProductDetailsNewDesign from 'common/ProductDetailsV2';
import { PRODUCT_TYPE } from 'constants/product';
import { AFFILIATE_PRODUCTS_VIEW } from 'utils/product';
import { CONSIGNMENT_TYPES } from 'constants/order';
import CampaignList from 'components/Campaign/CampaignList';
import ServiceList from 'components/Services/ServiceList';
import ServiceDetails from 'components/Services/ServiceDetails';
import OrganizationList from 'components/Organization/BusinessUnitsView';
import BusinessUnitDetails from 'components/Organization/BusinessUnitDetails';
import Settings from 'components/Settings/Settings';
import OnboardingPage from 'OnboardingComponents/OnboardingPage';
import BrandDetailsV2 from 'components/Brand/BrandDetailsV2';
import BrandsV2 from 'components/Brand/BrandsV2';

// Types
export type UserRole = typeof ROUTES_VIEWER_ROLE.ADMIN | typeof ROUTES_VIEWER_ROLE.EDITOR;

export interface RouteProps {
  selectedVendor: Vendor;
  updateVendor: (name: string) => void;
  updateBusinessUnit: (id: number) => void;
  vendorsList: Vendor[];
  featureFlags: FeatureFlag;
  userPreferences: UserPreferences;
  userPreferencesReq: AsyncState<any>;
  user: User;
  selectedBusinessUnit: BusinessUnit;
  setVendorsList?: (vendors: Vendor[]) => void;
}

// Utility functions for route creation
const createProtectedRoute = (
  Component: React.ComponentType<any>,
  requiredFlag: FeatureFlagValues,
  FallbackComponent: React.ComponentType<any> = NotFound
) => (props: RouteProps) =>
    grantAccess(props.featureFlags, requiredFlag, props.user)
      ? <Component {...props} />
      : <FallbackComponent />;

// Route configuration factory
export const createRoutes = (userRole: UserRole) => {
  const isAdmin = userRole === ROUTES_VIEWER_ROLE.ADMIN;
  const isEditor = userRole === ROUTES_VIEWER_ROLE.EDITOR;

  const routes: Record<string, any> = {
    // Dashboard
    '/': () => ({ selectedVendor, vendorsList, selectedBusinessUnit, featureFlags }: RouteProps) =>
      <Dashboard selectedVendor={selectedVendor} vendorsList={vendorsList} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} />,

    // Account Settings
    '/account*': () => () => <AccountSettings />,

    // Brands
    '/brands': () => ({ selectedVendor, user, featureFlags, selectedBusinessUnit }: RouteProps) => <BrandsV2 selectedVendor={selectedVendor} user={user} featureFlags={featureFlags} selectedBusinessUnit={selectedBusinessUnit} />,

    '/brands/brand-details/:id': ({ id }: any) => ({ selectedVendor, vendorsList, updateVendor, featureFlags, selectedBusinessUnit }: RouteProps) =>
      <BrandDetailsV2 brandId={id} selectedVendor={selectedVendor} vendorsList={vendorsList} updateVendor={updateVendor} featureFlags={featureFlags} selectedBusinessUnit={selectedBusinessUnit} />,

    '/brands/new-brand': () => ({ selectedVendor, vendorsList, updateVendor, featureFlags, selectedBusinessUnit }: RouteProps) =>
      <BrandDetailsV2 selectedVendor={selectedVendor} vendorsList={vendorsList} updateVendor={updateVendor} featureFlags={featureFlags} selectedBusinessUnit={selectedBusinessUnit} />,

    // Products
    '/products': () => ({ vendorsList, selectedVendor, selectedBusinessUnit, featureFlags }: RouteProps) =>
      <ProductListV2 key="products" vendorsList={vendorsList} selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} />,

    // Affiliates
    '/affiliates/program': () => ({ vendorsList, selectedVendor, selectedBusinessUnit, featureFlags, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <ProductListV2 key="affiliates-program" vendorsList={vendorsList} selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} productType={PRODUCT_TYPE.AFFILIATE} />
        : <NotFound />,

    '/affiliates/products': () => ({ vendorsList, selectedVendor, selectedBusinessUnit, featureFlags, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <ProductListV2 key="affiliates-products" vendorsList={vendorsList} selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} productType={AFFILIATE_PRODUCTS_VIEW} />
        : <NotFound />,
    '/affiliate-members': () => ({ selectedVendor, selectedBusinessUnit, userPreferences, userPreferencesReq, featureFlags, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <MembersList selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} userPreferences={userPreferences} userPreferencesReq={userPreferencesReq} featureFlags={featureFlags} />
        : <NotFound />,

    '/affiliate-members/page/:page': ({ page }: any) => ({ selectedVendor, selectedBusinessUnit, userPreferences, userPreferencesReq, featureFlags, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <MembersList selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} page={page} userPreferences={userPreferences} userPreferencesReq={userPreferencesReq} featureFlags={featureFlags} />
        : <NotFound />,

    '/affiliate-members/:memberId': ({ memberId }: any) => ({ selectedVendor, selectedBusinessUnit, featureFlags, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <MemberDetails memberId={memberId} selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} />
        : <NotFound />,

    '/affiliates/orders': () => ({ selectedBusinessUnit, selectedVendor, featureFlags, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <OrdersList selectedBusinessUnit={selectedBusinessUnit} selectedVendor={selectedVendor} featureFlags={featureFlags} consignmentType={CONSIGNMENT_TYPES.AFFILIATE_PROGRAM} headingText="Affiliate Orders" baseRoute="affiliates/orders" />
        : <NotFound />,

    '/affiliates/orders/page/:page': ({ page }: any) => ({ selectedBusinessUnit, selectedVendor, featureFlags, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <OrdersList selectedBusinessUnit={selectedBusinessUnit} selectedVendor={selectedVendor} featureFlags={featureFlags} consignmentType={CONSIGNMENT_TYPES.AFFILIATE_PROGRAM} headingText="Affiliate Orders" baseRoute="affiliates/orders" page={page} />
        : <NotFound />,

    '/affiliates/orders/order-details/:id?': ({ id }: any) => ({ featureFlags, updateVendor, selectedBusinessUnit, selectedVendor, vendorsList, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <OrderDetails consignmentId={'cons' + id} featureFlags={featureFlags} updateVendor={updateVendor} selectedBusinessUnit={selectedBusinessUnit} selectedVendor={selectedVendor} vendorsList={vendorsList} />
        : <NotFound />,

    '/affiliates/orders/split-order-details/:id': ({ id }: any) => ({ featureFlags, user, selectedBusinessUnit }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <SplitOrderContainer orderId={id} featureFlags={featureFlags} />
        : <NotFound />,

    '/affiliates/settings': () => ({ selectedVendor, selectedBusinessUnit, featureFlags, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <AffiliateSettings selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} />
        : <NotFound />,

    ...createAffiliateRoutes(),

    // Subscriptions
    '/subscriptions/products': () => ({ vendorsList, selectedVendor, selectedBusinessUnit, featureFlags, user }: RouteProps) =>
      (hasOfmpAdminProfile(user) || selectedBusinessUnit?.isSubscriptionEnabled) && grantAccess(featureFlags, FeatureFlagValues.SUBSCRIPTION_PRODUCT, user)
        ? <ProductListV2 key="subscriptions-products" vendorsList={vendorsList} selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} productType={PRODUCT_TYPE.SUBSCRIPTION} />
        : <NotFound />,

    '/subscriptions/orders': () => ({ selectedBusinessUnit, selectedVendor, featureFlags, user }: RouteProps) =>
      (hasOfmpAdminProfile(user) || selectedBusinessUnit?.isSubscriptionEnabled) && grantAccess(featureFlags, FeatureFlagValues.SUBSCRIPTION_PRODUCT, user)
        ? <OrdersList selectedBusinessUnit={selectedBusinessUnit} selectedVendor={selectedVendor} featureFlags={featureFlags} consignmentType={CONSIGNMENT_TYPES.SUBSCRIPTION} headingText="Subscription Orders" baseRoute="subscriptions/orders" />
        : <NotFound />,

    '/subscriptions/orders/page/:page': ({ page }: any) => ({ selectedBusinessUnit, selectedVendor, featureFlags, user }: RouteProps) =>
      (hasOfmpAdminProfile(user) || selectedBusinessUnit?.isSubscriptionEnabled) && grantAccess(featureFlags, FeatureFlagValues.SUBSCRIPTION_PRODUCT, user)
        ? <OrdersList selectedBusinessUnit={selectedBusinessUnit} selectedVendor={selectedVendor} featureFlags={featureFlags} consignmentType={CONSIGNMENT_TYPES.SUBSCRIPTION} headingText="Subscription Orders" baseRoute="subscriptions/orders" page={page} />
        : <NotFound />,

    // Subscription order details
    '/subscriptions/orders/order-details/:id?': ({ id }: any) => ({ featureFlags, updateVendor, selectedBusinessUnit, selectedVendor, vendorsList, user }: RouteProps) =>
      (hasOfmpAdminProfile(user) || selectedBusinessUnit?.isSubscriptionEnabled) && grantAccess(featureFlags, FeatureFlagValues.SUBSCRIPTION_PRODUCT, user)
        ? <OrderDetails consignmentId={'cons' + id} featureFlags={featureFlags} updateVendor={updateVendor} selectedBusinessUnit={selectedBusinessUnit} selectedVendor={selectedVendor} vendorsList={vendorsList} />
        : <NotFound />,

    '/subscriptions/orders/split-order-details/:id': ({ id }: any) => ({ featureFlags, user, selectedBusinessUnit }: RouteProps) =>
      (hasOfmpAdminProfile(user) || selectedBusinessUnit?.isSubscriptionEnabled) && grantAccess(featureFlags, FeatureFlagValues.SUBSCRIPTION_PRODUCT, user)
        ? <SplitOrderContainer orderId={id} featureFlags={featureFlags} />
        : <NotFound />,

    // Subscription Details with variants
    ...createSubscriptionRoutes(),

    // Product Details with variants
    ...createProductRoutes(),

    // Warehouses
    '/warehouses': () => ({ selectedVendor, updateVendor, vendorsList, featureFlags }: RouteProps) =>
      <WarehouseList selectedVendor={selectedVendor} vendorsList={vendorsList} updateVendor={updateVendor} featureFlags={featureFlags} />,

    // Reports
    '/reports': () => ({ selectedVendor, featureFlags, vendorsList, selectedBusinessUnit }: RouteProps) =>
      <PowerBiReports selectedVendor={selectedVendor} vendorsList={vendorsList} featureFlags={featureFlags} selectedBusinessUnit={selectedBusinessUnit} />,

    // Categories & Attributes
    '/categories': () => () => <CategoriesList />,
    '/attributes': () => (props: RouteProps) =>
      grantAccess(props.featureFlags, FeatureFlagValues.BUSINESS_UNIT, props.user)
        ? <AttributesListV2 selectedVendor={props.selectedVendor} selectedBusinessUnit={props.selectedBusinessUnit} />
        : <AttributesList selectedVendor={props.selectedVendor} />,

    // Orders
    '/orders': () => ({ selectedBusinessUnit, selectedVendor, featureFlags }: RouteProps) =>
      <OrdersList selectedBusinessUnit={selectedBusinessUnit} selectedVendor={selectedVendor} featureFlags={featureFlags} />,

    '/orders/page/:page': ({ page }: any) => ({ selectedBusinessUnit, featureFlags, selectedVendor }: RouteProps) =>
      <OrdersList selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} selectedVendor={selectedVendor} page={page} />,

    // Order Details
    ...createOrderRoutes(),

    // Companies
    '/companies': () => ({ selectedVendor, selectedBusinessUnit, userPreferences, userPreferencesReq }: RouteProps) =>
      <CompaniesList selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} userPreferences={userPreferences} userPreferencesReq={userPreferencesReq} />,

    '/companies/page/:page': ({ page }: any) => ({ selectedVendor, selectedBusinessUnit, userPreferences, userPreferencesReq }: RouteProps) =>
      <CompaniesList selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} page={page} userPreferences={userPreferences} userPreferencesReq={userPreferencesReq} />,

    '/companies/company-details/:id': ({ id }: any) => (props: RouteProps) =>
      <CompanyDetailsV2 {...props} companyId={id} />,

    // Customers
    '/customers': () => ({ selectedVendor, selectedBusinessUnit, userPreferences, userPreferencesReq, featureFlags }: RouteProps) =>
      <CustomersList selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} userPreferences={userPreferences} userPreferencesReq={userPreferencesReq} featureFlags={featureFlags} />,

    '/customers/page/:page': ({ page }: any) => ({ selectedVendor, selectedBusinessUnit, userPreferences, userPreferencesReq, featureFlags }: RouteProps) =>
      <CustomersList selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} page={page} userPreferences={userPreferences} userPreferencesReq={userPreferencesReq} featureFlags={featureFlags} />,

    '/customers/customer-details/:id': ({ id }: any) => ({ featureFlags }: RouteProps) =>
      <CustomerDetails customerId={id} featureFlags={featureFlags} />,

    // Pricebooks
    '/pricebooks': () => ({ selectedVendor, selectedBusinessUnit, featureFlags }: RouteProps) =>
      <PricebooksTab selectedVendor={selectedVendor} isPricebookPage={true} companyCustomers={[]} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} />,

  // Marketing
  '/marketing': () => ({ selectedVendor, vendorsList }: RouteProps) =>
    <CampaignList selectedVendor={selectedVendor} vendorsList={vendorsList} />,

    // Services/Addons
    '/addons': () => ({ selectedVendor, selectedBusinessUnit, featureFlags }: RouteProps) =>
      <ServiceList selectedVendor={selectedVendor} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} />,

    '/addons/addon-details/:id': ({ id }: any) => ({ selectedVendor, vendorsList, updateBusinessUnit, selectedBusinessUnit, featureFlags }: RouteProps) =>
      <ServiceDetails serviceId={id} selectedVendor={selectedVendor} vendorsList={vendorsList} updateBusinessUnit={updateBusinessUnit} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} />,

    '/addons/new-addon': () => ({ selectedVendor, vendorsList, updateVendor, selectedBusinessUnit, featureFlags }: RouteProps) =>
      <ServiceDetails selectedVendor={selectedVendor} vendorsList={vendorsList} updateVendor={updateVendor} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} />,

          // Settings
          '/settings': () => ({ selectedVendor, featureFlags, selectedBusinessUnit }: RouteProps) =>
            <Settings selectedVendor={selectedVendor} featureFlags={featureFlags} selectedBusinessUnit={selectedBusinessUnit} />,

            // Organizations
            '/organizations': () => (props: RouteProps) =>
              createProtectedRoute(
                ({ selectedVendor, user, selectedBusinessUnit, featureFlags, vendorsList }) =>
                  <OrganizationList selectedVendor={selectedVendor} user={user} selectedBusinessUnit={selectedBusinessUnit} featureFlags={featureFlags} vendorsList={vendorsList} />,
                FeatureFlagValues.BUSINESS_UNIT
              )(props),

              '/organizations/organizations-details/:id': ({ id }: any) => (props: RouteProps) =>
                createProtectedRoute(
                  ({ selectedVendor, featureFlags, selectedBusinessUnit }) => <BusinessUnitDetails businessUnitId={id} selectedVendor={selectedVendor} featureFlags={featureFlags} selectedBusinessUnit={selectedBusinessUnit} />,
                  FeatureFlagValues.BUSINESS_UNIT
                )(props),

  };

// Add admin-only routes
if (isAdmin) {
  routes['/vendors'] = () => () => <VendorsList />;
  routes['/vendors/create'] = () => ({ featureFlags }: RouteProps) => <UpsertVendor featureFlags={featureFlags} />;
  routes['/vendors/update/:id'] = ({ id }: any) => ({ setVendorsList, featureFlags }: any) =>
    <UpsertVendor featureFlags={featureFlags} vendorId={id} setVendorsListCallBack={setVendorsList} />;
  routes['/users'] = () => ({ vendorsList, featureFlags }: RouteProps) => <UsersList vendorsList={vendorsList} featureFlags={featureFlags} />;
}

// Add editor-only routes
if (isEditor) {
  routes['/products/product-details/:id/view'] = ({ id }: any) => () => <ProductReview productId={id} />;
  routes['/onboarding/welcome'] = () => () => <OnboardingPage view='welcome' />;
  routes['/onboarding/process-guidelines'] = () => () => <OnboardingPage view='process-guidelines' />;
  routes['/onboarding/product-guidelines'] = () => () => <OnboardingPage view='product-guidelines' />;
  routes['/onboarding/seller-guidelines'] = () => () => <OnboardingPage view='seller-guidelines' />;
  routes['/onboarding/brand-communication'] = () => () => <OnboardingPage view='brand-communication' />;
  routes['/onboarding/promo-resources'] = () => () => <OnboardingPage view='promo-resources' />;
  routes['/onboarding*'] = () => () => <OnboardingPage view='' />;
}

// Catch-all
routes['*'] = () => () => <NotFound />;

return routes;
};

// Helper function to create subscription routes
function createSubscriptionRoutes() {
  const views = ['', 'variants', 'preview', 'warehouses'];
  const routes: Record<string, any> = {};

  // New subscription routes
  views.forEach(view => {
    const path = view ? `/subscriptions/new-subscription/${view}` : '/subscriptions/new-subscription';
    const viewProp = view || 'newProduct';

    routes[path] = () => ({ selectedVendor, vendorsList, updateVendor, featureFlags, selectedBusinessUnit, user }: RouteProps) =>
      (hasOfmpAdminProfile(user) || selectedBusinessUnit?.isSubscriptionEnabled) && grantAccess(featureFlags, FeatureFlagValues.SUBSCRIPTION_PRODUCT, user)
        ? <ProductDetailsNewDesign
            view={viewProp as any}
            selectedVendor={selectedVendor}
            vendorsList={vendorsList}
            updateVendor={updateVendor}
            featureFlags={featureFlags}
            selectedBusinessUnit={selectedBusinessUnit}
            productType={PRODUCT_TYPE.SUBSCRIPTION}
          />
        : <NotFound />;
  });

  // Existing subscription routes
  views.forEach(view => {
    const path = view ? `/subscriptions/subscription-details/:id/${view}` : '/subscriptions/subscription-details/:id';

    routes[path] = ({ id }: any) => ({ selectedVendor, vendorsList, updateVendor, featureFlags, selectedBusinessUnit, user }: RouteProps) =>
      (hasOfmpAdminProfile(user) || selectedBusinessUnit?.isSubscriptionEnabled) && grantAccess(featureFlags, FeatureFlagValues.SUBSCRIPTION_PRODUCT, user)
        ? <ProductDetailsNewDesign
            view={view as any || undefined}
            productId={id}
            selectedVendor={selectedVendor}
            vendorsList={vendorsList}
            updateVendor={updateVendor}
            featureFlags={featureFlags}
            selectedBusinessUnit={selectedBusinessUnit}
            productType={PRODUCT_TYPE.SUBSCRIPTION}
          />
        : <NotFound />;
  });

  return routes;
}

function createAffiliateRoutes() {
  const views = ['', 'variants', 'preview', 'warehouses', 'affiliate-form'];
  const routes: Record<string, any> = {};

  views.forEach(view => {
    const path = view ? `/affiliates/new-affiliate-program/${view}` : '/affiliates/new-affiliate-program';
    const viewProp = view || 'newProduct';
    routes[path] = () => ({ selectedVendor, vendorsList, updateVendor, featureFlags, selectedBusinessUnit, user }: RouteProps) =>
       grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <ProductDetailsNewDesign
            view={viewProp as any}
            selectedVendor={selectedVendor}
            vendorsList={vendorsList}
            updateVendor={updateVendor}
            featureFlags={featureFlags}
            selectedBusinessUnit={selectedBusinessUnit}
            productType={PRODUCT_TYPE.AFFILIATE}
          />
        : <NotFound />;
  });

  views.forEach(view => {
    const path = view ? `/affiliates/program-details/:id/${view}` : '/affiliates/program-details/:id';
    routes[path] = ({ id }: any) => ({ selectedVendor, vendorsList, updateVendor, featureFlags, selectedBusinessUnit, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.AFFILIATE_PROGRAM, user)
        ? <ProductDetailsNewDesign
            view={view as any || undefined}
            productId={id}
            selectedVendor={selectedVendor}
            vendorsList={vendorsList}
            updateVendor={updateVendor}
            featureFlags={featureFlags}
            selectedBusinessUnit={selectedBusinessUnit}
            productType={PRODUCT_TYPE.AFFILIATE}
          />
        : <NotFound />;
  });

  return routes;
}

// Helper function to create product routes
function createProductRoutes() {
  const productViews = ['', 'variants', 'preview', 'warehouses', 'smart-variants'];
  const routes: Record<string, any> = {};

  // New product routes
  productViews.forEach(view => {
    const path = view ? `/products/new-product/${view}` : '/products/new-product';
    const viewProp = view || 'newProduct';

    routes[path] = () => ({ selectedVendor, vendorsList, updateVendor, featureFlags, selectedBusinessUnit }: RouteProps) =>
      <ProductDetailsNewDesign
        view={viewProp as any}
        selectedVendor={selectedVendor}
        vendorsList={vendorsList}
        updateVendor={updateVendor}
        featureFlags={featureFlags}
        selectedBusinessUnit={selectedBusinessUnit}
      />;
  });

  // Existing product routes
  productViews.forEach(view => {
    const path = view ? `/products/product-details/:id/${view}` : '/products/product-details/:id';

    routes[path] = ({ id }: any) => ({ selectedVendor, vendorsList, updateVendor, featureFlags, selectedBusinessUnit }: RouteProps) =>
      <ProductDetailsNewDesign
        view={view as any || undefined}
        productId={id}
        selectedVendor={selectedVendor}
        vendorsList={vendorsList}
        updateVendor={updateVendor}
        featureFlags={featureFlags}
        selectedBusinessUnit={selectedBusinessUnit}
      />;
  });

  return routes;
}

// Helper function to create order routes
function createOrderRoutes() {
  return {
    '/orders/order-details/:id/edit-order': ({ id }: any) => ({ featureFlags, user }: RouteProps) =>
      grantAccess(featureFlags, FeatureFlagValues.EDIT_ORDER, user)
        ? <EditOrderContainer consignmentId={'cons' + id} />
        : <NotFound />,

    '/orders/order-details/:id?': ({ id }: any) => ({ featureFlags, updateVendor, selectedBusinessUnit, selectedVendor, vendorsList }: RouteProps) =>
      <OrderDetails consignmentId={'cons' + id} featureFlags={featureFlags} updateVendor={updateVendor} selectedBusinessUnit={selectedBusinessUnit} selectedVendor={selectedVendor} vendorsList={vendorsList} />,

    '/orders/order-details/:id/document/:documentId': ({ id }: any) => ({ selectedVendor }: RouteProps) =>
      <OrderDocuments consignmentId={'cons' + id} selectedVendor={selectedVendor} />,

    '/orders/order-details/:id/new-document': ({ id }: any) => ({ vendorsList, selectedVendor }: RouteProps) =>
      <OrderDocuments consignmentId={'cons' + id} selectedVendor={selectedVendor} vendorsList={vendorsList} />,

    '/orders/split-order-details/:id': ({ id }: any) => ({ featureFlags }: RouteProps) =>
      <SplitOrderContainer orderId={id} featureFlags={featureFlags} />,
  };
}

export default createRoutes;